import { Injectable } from '@angular/core';

import { filter, map } from 'rxjs';

import { CamBaseService, GraphEndpoint, GraphReponsePaged, HandleComplexRequest } from '@camelot/server';
import { isNonNullable } from '@camelot/utils';

import { UnavailabilityCreationPayloadInput } from './dto/post/unavailability-creation-payload-input';
import { UnavailabilityModifierPayloadInput } from './dto/post/unavailability-modifier-payload-input';
import { CalendarUnavailability, calendarUnavailabilityProps } from './dto/unavailability';
import { CREATE_UNAVAILABILITY, UPDATE_UNAVAILABILITY } from './mutations';
import { GET_CALENDAR_UNAVAILABILITIES } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'planningService',
  endpoint: 'planning',
};

@Injectable({
  providedIn: 'root',
})
export class CamUnavailabilitiesService extends CamBaseService {
  public unavailabilities = new HandleComplexRequest<CalendarUnavailability[]>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchUnavailabilities$(userId: string, startDate: Date, endDate: Date) {
    return this.unavailabilities.fetch(
      userId,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarUnavailability>>(
          GET_CALENDAR_UNAVAILABILITIES({
            where: { userId: { eq: userId }, start: { gte: startDate.toISOString(), lte: endDate.toISOString() } },
            props: `items {
                ${calendarUnavailabilityProps.get('id')}
                ${calendarUnavailabilityProps.get('start')}
                ${calendarUnavailabilityProps.get('end')}
                ${calendarUnavailabilityProps.get('title')}
            }`,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchUnavailabilitiesForUsers$(usersId: string[], startDate: Date, endDate: Date) {
    return this.unavailabilities.fetch(
      usersId.join('-'),
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarUnavailability>>(
          GET_CALENDAR_UNAVAILABILITIES({
            where: { userId: { in: usersId }, start: { gte: startDate.toISOString(), lte: endDate.toISOString() } },
            props: `items {
                ${calendarUnavailabilityProps.get('id')}
                ${calendarUnavailabilityProps.get('start')}
                ${calendarUnavailabilityProps.get('end')}
                ${calendarUnavailabilityProps.get('title')}
                ${calendarUnavailabilityProps.get('userId')}
            }`,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public createUnavailability$(unavailability: UnavailabilityCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<CalendarUnavailability>(CREATE_UNAVAILABILITY(unavailability), graphEndpoint.clientName, [
        'unavailabilities',
      ])
      .pipe(filter(isNonNullable));
  }

  public update$(visit: Partial<UnavailabilityModifierPayloadInput>) {
    return this._graphService.mutateBuilder<CalendarUnavailability>(
      UPDATE_UNAVAILABILITY(visit),
      graphEndpoint.clientName,
      ['unavailabilities']
    );
  }
}
